/**
 * Controller ratings: what the Academy certifies. A rating is derived from the lesson list, never
 * stored as truth, so adding a lesson tightens the rating it belongs to. Ratings already awarded
 * are remembered separately, so a change to the list cannot take one back from a player.
 */
import type { Lesson, LessonTier } from './lesson';
import { isPassed, type AcademyProgress } from './progress';

export interface Badge {
  id: string;
  title: string;
  /** What it certifies, printed under the seal. */
  detail: string;
  /** One to two characters for the stamp face. */
  stamp: string;
  /** Lessons that must all be passed, given the current list. */
  requires(lessons: readonly Lesson[]): Lesson[];
}

const ofTier = (tier: LessonTier) => (lessons: readonly Lesson[]) => lessons.filter((l) => l.tier === tier);
const ofAirport = (icao: string) => (lessons: readonly Lesson[]) => lessons.filter((l) => l.airport === icao);

export const BADGES: readonly Badge[] = [
  { id: 'tier-beginner', title: 'Student controller', detail: 'Every beginner exercise passed.', stamp: 'S', requires: ofTier('beginner') },
  { id: 'tier-mid', title: 'Rated controller', detail: 'Every intermediate exercise passed.', stamp: 'R', requires: ofTier('mid') },
  { id: 'tier-pro', title: 'Senior controller', detail: 'Every advanced exercise passed.', stamp: 'P', requires: ofTier('pro') },
  { id: 'rating-llbg', title: 'Ben Gurion rating', detail: 'Every LLBG exercise passed.', stamp: 'LL', requires: ofAirport('llbg') },
  { id: 'rating-ksfo', title: 'San Francisco rating', detail: 'Every KSFO exercise passed.', stamp: 'SF', requires: ofAirport('ksfo') },
  { id: 'academy-complete', title: 'Academy graduate', detail: 'Every exercise in the Academy passed.', stamp: 'A', requires: (lessons) => [...lessons] },
];

/** A rating is earned when every lesson it requires has been passed. An empty requirement never earns. */
export function isEarned(badge: Badge, progress: AcademyProgress, lessons: readonly Lesson[]): boolean {
  const required = badge.requires(lessons);
  return required.length > 0 && required.every((l) => isPassed(progress, l.id));
}

/** Earned now, plus anything awarded before; never shrinks as the lesson list grows. */
export function earnedBadges(progress: AcademyProgress, lessons: readonly Lesson[]): Badge[] {
  return BADGES.filter((b) => progress.badges.includes(b.id) || isEarned(b, progress, lessons));
}

/** How many of a rating's lessons are done, for the progress line under an unearned seal. */
export function badgeProgress(badge: Badge, progress: AcademyProgress, lessons: readonly Lesson[]): { done: number; total: number } {
  const required = badge.requires(lessons);
  return { done: required.filter((l) => isPassed(progress, l.id)).length, total: required.length };
}

/** The ids to persist after a pass, so a later change to the lesson list cannot revoke one. */
export const awardedBadgeIds = (progress: AcademyProgress, lessons: readonly Lesson[]): string[] =>
  [...new Set([...progress.badges, ...BADGES.filter((b) => isEarned(b, progress, lessons)).map((b) => b.id)])];
